import { TutorialLayout, CodePreview } from '../../../../shared/components';
import { tutorialLevels } from '../../../../shared/config/tutorials';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Info, FolderTree } from 'lucide-react';

const MultiTemplate = () => {
  return (
    <TutorialLayout levels={tutorialLevels}>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold mb-4">Multi Template</h1>
          <p className="text-lg text-muted-foreground">
            Structured multi-page application with explicit route definitions.
          </p>
        </div>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription>
            Best for: Medium applications where you want explicit control over routes and a clear page structure.
          </AlertDescription>
        </Alert>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Create a Project</h2>

          <CodePreview
            title="Scaffold Multi Template"
            code={`npx @voilajsx/uikit create my-app --multi

cd my-app
npm run dev`}
            language="bash"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Project Structure</h2>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FolderTree className="w-5 h-5" />
                Directory Layout
              </CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-sm font-mono bg-muted p-4 rounded-lg overflow-x-auto">
{`my-app/
├── public/
├── src/
│   ├── components/         # Shared components
│   │   ├── Header.tsx
│   │   ├── Footer.tsx
│   │   └── Sidebar.tsx
│   ├── layouts/            # Layout wrappers
│   │   ├── MainLayout.tsx
│   │   └── AuthLayout.tsx
│   ├── pages/              # Page components
│   │   ├── home/
│   │   │   └── index.tsx
│   │   ├── dashboard/
│   │   │   ├── index.tsx
│   │   │   └── settings.tsx
│   │   └── auth/
│   │       ├── login.tsx
│   │       └── register.tsx
│   ├── routes/             # Route definitions
│   │   └── index.tsx
│   ├── App.tsx
│   ├── main.tsx
│   └── index.css
└── package.json`}
              </pre>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Route Configuration</h2>

          <CodePreview
            title="src/routes/index.tsx"
            code={`import { RouteObject } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import AuthLayout from '../layouts/AuthLayout';

// Pages
import Home from '../pages/home';
import Dashboard from '../pages/dashboard';
import DashboardSettings from '../pages/dashboard/settings';
import Login from '../pages/auth/login';
import Register from '../pages/auth/register';
import NotFound from '../pages/NotFound';

export const routes: RouteObject[] = [
  // Public routes with main layout
  {
    element: <MainLayout />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/about', element: <About /> },
    ],
  },

  // Dashboard routes (protected)
  {
    element: <MainLayout sidebar />,
    children: [
      { path: '/dashboard', element: <Dashboard /> },
      { path: '/dashboard/settings', element: <DashboardSettings /> },
    ],
  },

  // Auth routes
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/register', element: <Register /> },
    ],
  },

  // 404
  { path: '*', element: <NotFound /> },
];`}
            language="tsx"
          />

          <CodePreview
            title="src/App.tsx"
            code={`import { BrowserRouter, useRoutes } from 'react-router-dom';
import { routes } from './routes';

function AppRoutes() {
  return useRoutes(routes);
}

function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

export default App;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Layouts</h2>

          <CodePreview
            title="src/layouts/MainLayout.tsx"
            code={`import { Outlet } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page-layout';
import { AdminLayout } from '@voilajsx/uikit/admin-layout';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Sidebar from '../components/Sidebar';

interface MainLayoutProps {
  sidebar?: boolean;
}

function MainLayout({ sidebar = false }: MainLayoutProps) {
  if (sidebar) {
    return (
      <AdminLayout
        sidebar={<Sidebar />}
        header={<Header />}
      >
        <Outlet />
      </AdminLayout>
    );
  }

  return (
    <PageLayout header={<Header />} footer={<Footer />}>
      <Outlet />
    </PageLayout>
  );
}

export default MainLayout;`}
            language="tsx"
          />

          <CodePreview
            title="src/layouts/AuthLayout.tsx"
            code={`import { Outlet } from 'react-router-dom';
import { AuthLayout as UIKitAuthLayout } from '@voilajsx/uikit/auth-layout';

function AuthLayout() {
  return (
    <UIKitAuthLayout
      logo={
        <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold">
          A
        </div>
      }
    >
      <Outlet />
    </UIKitAuthLayout>
  );
}

export default AuthLayout;`}
            language="tsx"
          />
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Multi vs SPA Template</h2>

          <div className="bg-muted/50 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-3 font-semibold">Feature</th>
                  <th className="text-left p-3 font-semibold">SPA</th>
                  <th className="text-left p-3 font-semibold">Multi</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border">
                  <td className="p-3">Route definition</td>
                  <td className="p-3 text-muted-foreground">Inline in App.tsx</td>
                  <td className="p-3 text-muted-foreground">Separate routes/ folder</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Layouts</td>
                  <td className="p-3 text-muted-foreground">Single layout</td>
                  <td className="p-3 text-muted-foreground">Multiple layouts</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="p-3">Page organization</td>
                  <td className="p-3 text-muted-foreground">Flat pages/</td>
                  <td className="p-3 text-muted-foreground">Nested by section</td>
                </tr>
                <tr>
                  <td className="p-3">Best for</td>
                  <td className="p-3 text-muted-foreground">Simple sites</td>
                  <td className="p-3 text-muted-foreground">Apps with auth/dashboard</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">When to Use Multi Template</h2>

          <div className="grid md:grid-cols-2 gap-4">
            <Card className="border-green-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-green-600">Good For</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Apps with public + protected sections</li>
                  <li>• Multiple layout needs (main, auth, admin)</li>
                  <li>• Explicit route control</li>
                  <li>• Medium-sized applications</li>
                  <li>• Teams familiar with route config</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-red-500/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-red-600">Consider FBCA Instead</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>• Large applications with many features</li>
                  <li>• Want auto-discovery routing</li>
                  <li>• Feature isolation is important</li>
                  <li>• Multiple developers/teams</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    </TutorialLayout>
  );
};

export default MultiTemplate;
